import { TrabaLoader } from '@traba/react-components'
import { theme } from '@traba/theme'
import { captureSentryError } from '@traba/utils'
import { useEffect, useState } from 'react'
import { isReloadPending, reloadPage } from 'src/reloadPage'
import styled from 'styled-components'

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.space.sm}px;
  padding: ${theme.space.med}px;
  font-family: ${theme.typography.fontFamily};
  text-align: center;
`

const Action = styled.button`
  padding: ${theme.space.xxs}px ${theme.space.xsmed}px;
  border: 1px solid ${theme.colors.Grey20};
  border-radius: ${theme.border.radius};
  background: ${theme.colors.White};
  cursor: pointer;
`

export function ErrorBoundaryFallback({ error }: { error?: Error }) {
  // A reload pending at mount means stale-build recovery already initiated a
  // hard reload — the error is moot, so show the update loader for the reload
  // round-trip instead of flashing the error screen.
  const [updateInFlight] = useState(isReloadPending)
  const [retryInFlight, setRetryInFlight] = useState(false)

  // react-error-boundary swallows caught errors, so Sentry never sees them
  // unless the fallback reports explicitly. Skip during update recovery:
  // successful stale-build heals would otherwise log noise on every deploy.
  useEffect(() => {
    if (error && !updateInFlight) {
      captureSentryError(error)
    }
  }, [error, updateInFlight])

  if (updateInFlight) {
    return <TrabaLoader caption="Updating Neo…" />
  }

  if (retryInFlight) {
    return <TrabaLoader />
  }

  const handleReload = () => {
    // Hard reload, not resetErrorBoundary: a rejected lazy import is pinned
    // in the document's module map, so a soft reset can never recover.
    reloadPage()
    setRetryInFlight(true)
  }

  return (
    <Page>
      <h1>Something went wrong</h1>
      <p>An unexpected error occurred. Please try again.</p>
      <Action onClick={handleReload}>Reload</Action>
    </Page>
  )
}
